import type { ValidationResult } from './MATRowDetailDrawer.types';
import type { MatDiscoveryItem, MatDiscoveryResponse } from '../../types/mat-types';

export const DISCOVERY_BY_KEY_PAGE_SIZE = 150;

type SanitizeMatRequestBodyOptions = {
  stripDataSourceStatus?: boolean;
};

/**
 * Validates that MatKey is present and all required fields are filled
 */
export function validateDraft(
  draft: Record<string, any>,
  requiredSet: Set<string>
): ValidationResult {
  const matKeyValue = draft['MatKey'];

  // Check MatKey first
  if (!matKeyValue || String(matKeyValue).trim() === '') {
    return {
      isValid: false,
      missingFields: ['MatKey'],
      errorMessage: 'MatKey is required.',
    };
  }

  // Check other required fields
  const missing: string[] = [];
  requiredSet.forEach((field) => {
    const value = draft[field];
    if (value == null || String(value).trim() === '') {
      missing.push(field);
    }
  });

  if (missing.length > 0) {
    const errorMessage = `${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required`;
    return {
      isValid: false,
      missingFields: missing,
      errorMessage,
    };
  }

  return {
    isValid: true,
    missingFields: [],
  };
}

/**
 * Gets success message based on whether row is discovered or existing
 */
export function getSuccessMessage(isDiscoveredRow: boolean): string {
  return isDiscoveredRow ? 'MAT entry created!' : 'MAT entry updated!';
}

/**
 * Removes UI-only keys from MAT payloads before sending them to the API.
 */
export function sanitizeMatRequestBody(
  body: Record<string, any>,
  { stripDataSourceStatus = false }: SanitizeMatRequestBodyOptions = {}
) {
  const sanitizedEntries = Object.entries(body).filter(([key]) => !key.startsWith('__'));
  const sanitized = Object.fromEntries(sanitizedEntries);

  if (stripDataSourceStatus) {
    const { DataSourceStatus, ...bodyWithoutStatus } = sanitized;
    return bodyWithoutStatus;
  }

  return sanitized;
}

/**
 * Gets submit button text based on state
 */
export function getSubmitButtonText(
  isLoading: boolean,
  blockEditing: boolean,
  isDiscoveredRow: boolean
): string {
  if (isLoading) return 'Submitting…';
  if (blockEditing) return 'Loading…';
  return isDiscoveredRow ? 'CREATE MAT ROW' : 'UPDATE MAT ROW';
}

/**
 * Picks the discovery entry belonging to a given MatKey.
 *
 * The discovery list endpoint filters MatKey by substring, so a request for a
 * short key also returns every longer key ending in it. Only an entry whose
 * key is equal to the requested one belongs to it.
 */
export function selectDiscoveryItemForKey(
  results: MatDiscoveryItem[] | undefined,
  matKey: string | null | undefined
): MatDiscoveryItem | undefined {
  if (!matKey) return undefined;
  return results?.find((item) => item.mat_key === matKey);
}

/**
 * Pages through the discovery list for a MatKey until the entry equal to it is
 * found or the pages run out. The substring filter can return more longer keys
 * than fit on one page, and those can sort ahead of the requested key.
 */
export async function findDiscoveryItemForKey(
  fetchPage: (params: {
    MatKey: string;
    limit: number;
    after?: string;
  }) => Promise<MatDiscoveryResponse>,
  matKey: string
): Promise<MatDiscoveryItem | undefined> {
  let after: string | undefined;
  for (;;) {
    const page = await fetchPage({
      MatKey: matKey,
      limit: DISCOVERY_BY_KEY_PAGE_SIZE,
      ...(after !== undefined ? { after } : {}),
    });
    const match = selectDiscoveryItemForKey(page.results, matKey);
    if (match || page.next === null || page.results.length === 0) return match;
    after = page.results[page.results.length - 1].mat_key;
  }
}
